import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { fetchAllPosts, urlFor, formatDateShort, getBadgeColors, SEGMENT_GRADIENT } from '../lib/sanityClient'

const SEGMENTS = [
  { label: 'All', value: '' },
  { label: 'For Photographers', value: 'photographers' },
  { label: 'For Local Businesses', value: 'local-businesses' },
  { label: 'For Real Estate', value: 'real-estate' },
  { label: 'For Event Venues', value: 'event-venues' },
]

const Blog = () => {
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeSegment, setActiveSegment] = useState('')

  useEffect(() => {
    fetchAllPosts().then(data => {
      setPosts(data || [])
      setLoading(false)
    })
  }, [])

  const filtered = activeSegment
    ? posts.filter(p => p.category?.segment === activeSegment)
    : posts

  return (
    <>
      <Helmet>
        <title>Blog | FrameFinder</title>
        <meta name="description" content="Photography industry insights, client acquisition strategies, and business tips for Denver photographers and the businesses that hire them." />
        <link rel="canonical" href="https://www.framefinder.io/blog" />
      </Helmet>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        :root {
          --ink: #0e0e0e; --ink-soft: #5a5a5a; --ink-faint: #a0a0a0;
          --paper: #f7f5f2; --white: #ffffff; --accent: #d4a843;
          --accent-soft: #f5ead0; --border: #e4e1db; --border-deep: #c8c3bb;
          --shadow-sm: 0 1px 4px rgba(14,14,14,0.06); --shadow-md: 0 4px 20px rgba(14,14,14,0.08);
          --r-sm: 8px; --r-md: 14px; --r-lg: 24px; --r-pill: 100px;
          --font-display: 'Instrument Serif', serif; --font-body: 'DM Sans', sans-serif;
        }
        body { font-family: var(--font-body); background: var(--paper); color: var(--ink); -webkit-font-smoothing: antialiased; }

        /* NAV */
        .bl-nav { position: sticky; top: 0; z-index: 100; background: rgba(247,245,242,0.92); backdrop-filter: blur(12px); border-bottom: 1px solid var(--border); height: 60px; display: flex; align-items: center; padding: 0 40px; }
        .bl-nav-inner { max-width: 1280px; margin: 0 auto; width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 24px; }
        .bl-nav-logo { font-family: var(--font-display); font-size: 18px; letter-spacing: -0.3px; color: var(--ink); text-decoration: none; }
        .bl-nav-actions { display: flex; align-items: center; gap: 8px; }
        .bl-btn { display: inline-flex; align-items: center; gap: 7px; font-family: var(--font-body); font-weight: 500; border: none; cursor: pointer; text-decoration: none; white-space: nowrap; transition: transform 0.15s, box-shadow 0.15s, background 0.15s; border-radius: var(--r-pill); }
        .bl-btn:hover { transform: translateY(-1px); }
        .bl-btn-ghost { font-size: 13px; padding: 7px 16px; background: var(--white); color: var(--ink); border: 1px solid var(--border); }
        .bl-btn-ghost:hover { background: var(--paper); }
        .bl-btn-primary { font-size: 13px; padding: 8px 18px; background: var(--ink); color: var(--white); box-shadow: 0 2px 10px rgba(14,14,14,0.16); }
        .bl-btn-primary:hover { box-shadow: 0 4px 16px rgba(14,14,14,0.24); }

        /* PAGE */
        .bl-page { max-width: 1280px; margin: 0 auto; padding: 0 40px 120px; }
        .bl-header { padding: 64px 0 48px; border-bottom: 1px solid var(--border); margin-bottom: 48px; }
        .bl-eyebrow { font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--accent); margin-bottom: 12px; }
        .bl-title { font-family: var(--font-display); font-size: clamp(36px, 4vw, 52px); letter-spacing: -2px; line-height: 1.1; color: var(--ink); margin-bottom: 16px; }
        .bl-subtitle { font-size: 17px; color: var(--ink-soft); line-height: 1.6; max-width: 520px; }

        /* FILTERS */
        .bl-filters { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 40px; }
        .bl-filter-btn { font-size: 13px; font-weight: 500; padding: 7px 16px; border-radius: var(--r-pill); border: 1px solid var(--border); background: var(--white); color: var(--ink-soft); cursor: pointer; transition: all 0.15s; }
        .bl-filter-btn:hover { border-color: var(--ink); color: var(--ink); }
        .bl-filter-btn.active { background: var(--ink); color: var(--white); border-color: var(--ink); }

        /* GRID */
        .bl-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        .bl-card { background: var(--white); border: 1px solid var(--border); border-radius: var(--r-lg); overflow: hidden; text-decoration: none; color: inherit; display: flex; flex-direction: column; transition: transform 0.2s, box-shadow 0.2s; }
        .bl-card:hover { transform: translateY(-3px); box-shadow: var(--shadow-md); }
        .bl-card-thumb { height: 180px; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .bl-card-thumb img { width: 100%; height: 100%; object-fit: cover; }
        .bl-card-body { padding: 22px 22px 16px; flex: 1; }
        .bl-card-badge { display: inline-flex; align-items: center; gap: 5px; font-size: 10px; font-weight: 700; letter-spacing: 0.5px; padding: 4px 10px; border-radius: var(--r-pill); margin-bottom: 12px; }
        .bl-card-badge-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
        .bl-card-title { font-family: var(--font-display); font-size: 18px; letter-spacing: -0.4px; line-height: 1.3; color: var(--ink); margin-bottom: 10px; }
        .bl-card-intro { font-size: 13px; color: var(--ink-soft); line-height: 1.6; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
        .bl-card-meta { padding: 12px 22px; border-top: 1px solid var(--border); font-size: 11px; color: var(--ink-faint); display: flex; justify-content: space-between; }
        .bl-empty { grid-column: 1/-1; text-align: center; padding: 80px 0; color: var(--ink-faint); font-size: 15px; }

        /* FOOTER */
        .bl-footer { background: var(--ink); padding: 48px 40px; margin-top: 0; }
        .bl-footer-inner { max-width: 1280px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 20px; }
        .bl-footer-logo { font-family: var(--font-display); font-size: 20px; color: var(--white); }
        .bl-footer-links { display: flex; align-items: center; gap: 4px; }
        .bl-footer-link { font-size: 12px; color: rgba(255,255,255,0.35); text-decoration: none; padding: 4px 10px; border-radius: 100px; transition: color 0.15s; }
        .bl-footer-link:hover { color: rgba(255,255,255,0.7); }
        .bl-footer-copy { font-size: 12px; color: rgba(255,255,255,0.2); }

        /* LOADING */
        .bl-loading { display: flex; align-items: center; justify-content: center; min-height: 400px; font-size: 14px; color: var(--ink-faint); }

        @media (max-width: 1000px) { .bl-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 640px) {
          .bl-nav { padding: 0 20px; }
          .bl-page { padding: 0 20px 80px; }
          .bl-grid { grid-template-columns: 1fr; }
          .bl-header { padding: 40px 0 32px; }
        }
      `}</style>

      {/* NAV */}
      <nav className="bl-nav">
        <div className="bl-nav-inner">
          <Link to="/" className="bl-nav-logo">FrameFinder</Link>
          <div className="bl-nav-actions">
            <Link to="/login" className="bl-btn bl-btn-ghost">Log in</Link>
            <Link to="/signup" className="bl-btn bl-btn-primary">
              Create your profile
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </Link>
          </div>
        </div>
      </nav>

      {/* PAGE */}
      <main>
        <div className="bl-page">
          <header className="bl-header">
            <div className="bl-eyebrow">FrameFinder Blog</div>
            <h1 className="bl-title">Insights for Denver's<br /><em style={{fontStyle:'italic',color:'var(--ink-soft)'}}>photo industry</em></h1>
            <p className="bl-subtitle">Strategy, market insights, and practical guides for Denver photographers and the businesses that hire them.</p>
          </header>

          {/* SEGMENT FILTERS */}
          <div className="bl-filters">
            {SEGMENTS.map(s => (
              <button
                key={s.value}
                className={`bl-filter-btn${activeSegment === s.value ? ' active' : ''}`}
                onClick={() => setActiveSegment(s.value)}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* POST GRID */}
          {loading ? (
            <div className="bl-loading">Loading posts...</div>
          ) : (
            <div className="bl-grid">
              {filtered.length === 0 ? (
                <div className="bl-empty">No posts yet in this category.</div>
              ) : filtered.map(post => {
                const badge = getBadgeColors(post.category)
                const gradient = SEGMENT_GRADIENT[post.category?.segment] || SEGMENT_GRADIENT['photographers']
                return (
                  <Link key={post.slug} to={`/blog/${post.slug}`} className="bl-card">
                    <div className="bl-card-thumb" style={{ background: gradient }}>
                      {post.heroImage && (
                        <img
                          src={urlFor(post.heroImage).width(600).height(360).url()}
                          alt={post.heroImageAlt || post.title}
                        />
                      )}
                    </div>
                    <div className="bl-card-body">
                      {post.category && (
                        <div className="bl-card-badge" style={{ background: badge.bg, color: badge.text }}>
                          <span className="bl-card-badge-dot" style={{ background: badge.dot }} />
                          {post.category.title}
                        </div>
                      )}
                      <h2 className="bl-card-title">{post.title}</h2>
                      {post.intro && <p className="bl-card-intro">{post.intro}</p>}
                    </div>
                    <div className="bl-card-meta">
                      <span>{formatDateShort(post.publishedAt)}</span>
                      <span>{post.readTime} min read</span>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      </main>

      {/* FOOTER */}
      <footer className="bl-footer">
        <div className="bl-footer-inner">
          <span className="bl-footer-logo">FrameFinder</span>
          <div className="bl-footer-links">
            <Link to="/" className="bl-footer-link">For Businesses</Link>
            <Link to="/?view=photographer" className="bl-footer-link">For Creatives</Link>
            <Link to="/blog" className="bl-footer-link">Blog</Link>
          </div>
          <span className="bl-footer-copy">&copy; 2026 FrameFinder &middot; Denver, CO</span>
        </div>
      </footer>
    </>
  )
}

export default Blog
